<template>
  <Teleport to="body">
    <Transition name="ease-survey-fade">
      <div v-if="visible" class="ease-survey-backdrop" role="presentation">
        <div
          class="ease-survey-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ease-survey-heading"
        >
          <h2 id="ease-survey-heading" class="ease-survey-heading">
            Votre avis nous aide à améliorer l’espace client
          </h2>
          <p class="ease-survey-lead">
            Après cette session, une question rapide : dans quelle mesure trouvez-vous
            <strong>facile d’utiliser</strong> cette application ?
          </p>

          <fieldset class="ease-survey-scale" :disabled="submitting">
            <legend class="visually-hidden">Facilité perçue de 1 à 5</legend>
            <div class="ease-survey-options">
              <label
                v-for="opt in options"
                :key="opt.value"
                class="ease-survey-option"
                :class="{ 'ease-survey-option--active': score === opt.value }"
              >
                <input
                  v-model.number="score"
                  type="radio"
                  name="ease-score"
                  :value="opt.value"
                  class="ease-survey-radio"
                />
                <span class="ease-survey-num">{{ opt.value }}</span>
                <span class="ease-survey-label">{{ opt.label }}</span>
              </label>
            </div>
            <div class="ease-survey-scale-hint" aria-hidden="true">
              <span>Très difficile</span>
              <span>Très facile</span>
            </div>
          </fieldset>

          <label class="ease-survey-comment-label" for="ease-survey-comment">
            Commentaire (facultatif)
          </label>
          <textarea
            id="ease-survey-comment"
            v-model.trim="comment"
            class="ease-survey-comment"
            rows="2"
            maxlength="500"
            placeholder="Une suggestion en une phrase…"
            :disabled="submitting"
          />

          <div class="ease-survey-actions">
            <button
              type="button"
              class="ease-survey-btn ease-survey-btn--ghost"
              :disabled="submitting"
              @click="onNever"
            >
              Ne plus afficher
            </button>
            <button
              type="button"
              class="ease-survey-btn ease-survey-btn--ghost"
              :disabled="submitting"
              @click="onLater"
            >
              Plus tard
            </button>
            <button
              type="button"
              class="ease-survey-btn ease-survey-btn--primary"
              :disabled="submitting || score == null"
              @click="onSubmit"
            >
              {{ submitting ? 'Envoi…' : 'Envoyer' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
  /** Identifiant pour regrouper les réponses (ex. zone de l’app) */
  context: {
    type: String,
    default: 'dashboard',
  },
  /** Délai minimum après entrée dans le layout avant proposition (ms) */
  delayMs: {
    type: Number,
    default: 90_000,
  },
  /** Nombre de « vues » (route courante + navigations) minimum avant proposition */
  minRouteChanges: {
    type: Number,
    default: 1,
  },
  /** Ne pas re-proposer avant X jours après une réponse */
  cooldownDaysAfterSubmit: {
    type: Number,
    default: 30,
  },
  /** Repousser de X jours si « Plus tard » */
  snoozeDays: {
    type: Number,
    default: 3,
  },
});

const STORAGE_KEY = 'patrimonea_ease_survey_v1';
const LOG_KEY = 'patrimonea_ease_survey_log_v1';

const route = useRoute();
const visible = ref(false);
const score = ref(null);
const comment = ref('');
const submitting = ref(false);
const routeChanges = ref(0);
const sessionReady = ref(false);

const options = [
  { value: 1, label: 'Très difficile' },
  { value: 2, label: 'Difficile' },
  { value: 3, label: 'Moyen' },
  { value: 4, label: 'Facile' },
  { value: 5, label: 'Très facile' },
];

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const o = JSON.parse(raw);
    return typeof o === 'object' && o !== null ? o : {};
  } catch {
    return {};
  }
}

function saveState(partial) {
  const next = { ...loadState(), ...partial };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota */
  }
}

function canOffer() {
  const s = loadState();
  if (s.permanentDismiss) return false;
  if (s.submittedAt) {
    const elapsed = Date.now() - new Date(s.submittedAt).getTime();
    const cool = props.cooldownDaysAfterSubmit * 86400000;
    if (elapsed < cool) return false;
  }
  if (s.dismissedUntil && Date.now() < new Date(s.dismissedUntil).getTime()) return false;
  return true;
}

function tryOpen() {
  if (visible.value || submitting.value) return;
  if (!sessionReady.value) return;
  if (!canOffer()) return;
  if (routeChanges.value < props.minRouteChanges) return;
  visible.value = true;
}

let timerId = null;
let escHandler = null;

onMounted(() => {
  timerId = window.setTimeout(() => {
    sessionReady.value = true;
    tryOpen();
  }, props.delayMs);
});

onUnmounted(() => {
  if (timerId != null) window.clearTimeout(timerId);
  if (escHandler) {
    window.removeEventListener('keydown', escHandler);
    escHandler = null;
  }
});

watch(
  () => route.fullPath,
  () => {
    routeChanges.value += 1;
    tryOpen();
  },
  { immediate: true }
);

watch(visible, (v) => {
  if (v) {
    escHandler = (e) => {
      if (e.key === 'Escape') onLater();
    };
    window.addEventListener('keydown', escHandler);
  } else if (escHandler) {
    window.removeEventListener('keydown', escHandler);
    escHandler = null;
  }
});

function appendLog(entry) {
  try {
    const raw = localStorage.getItem(LOG_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const arr = Array.isArray(list) ? list : [];
    arr.push(entry);
    while (arr.length > 40) arr.shift();
    localStorage.setItem(LOG_KEY, JSON.stringify(arr));
  } catch {
    /* ignore */
  }
}

function onLater() {
  const until = new Date();
  until.setDate(until.getDate() + props.snoozeDays);
  saveState({ dismissedUntil: until.toISOString() });
  visible.value = false;
}

function onNever() {
  saveState({ permanentDismiss: true });
  visible.value = false;
}

function onSubmit() {
  if (score.value == null || submitting.value) return;
  submitting.value = true;
  const payload = {
    score: score.value,
    comment: comment.value || null,
    context: props.context,
    path: route.path,
    at: new Date().toISOString(),
  };
  saveState({
    submittedAt: payload.at,
    lastScore: payload.score,
    lastComment: payload.comment,
    dismissedUntil: null,
  });
  appendLog(payload);
  submitting.value = false;
  visible.value = false;
  score.value = null;
  comment.value = '';
}
</script>

<style scoped>
.ease-survey-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(10, 26, 47, 0.45);
  backdrop-filter: blur(4px);
}

.ease-survey-card {
  width: 100%;
  max-width: 28rem;
  padding: 1.35rem 1.35rem 1.15rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(212, 175, 55, 0.28);
  box-shadow: 0 20px 50px rgba(10, 26, 47, 0.18);
  font-family: var(--font-primary);
  color: rgba(10, 26, 47, 0.92);
}

.ease-survey-heading {
  margin: 0 0 0.5rem;
  font-family: var(--font-display), var(--font-primary);
  font-size: 1.15rem;
  font-weight: var(--font-weight-semibold);
  line-height: 1.35;
  letter-spacing: 0.01em;
}

.ease-survey-lead {
  margin: 0 0 1rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: var(--bank-gray-600);
}

.ease-survey-scale {
  border: none;
  margin: 0 0 0.75rem;
  padding: 0;
}

.ease-survey-options {
  display: flex;
  justify-content: space-between;
  gap: 0.35rem;
}

.ease-survey-option {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  padding: 0.45rem 0.2rem;
  border-radius: 10px;
  border: 1px solid rgba(10, 26, 47, 0.12);
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    box-shadow 0.15s ease;
}

.ease-survey-option:hover {
  border-color: rgba(212, 175, 55, 0.45);
}

.ease-survey-option--active {
  border-color: rgba(212, 175, 55, 0.75);
  background: rgba(212, 175, 55, 0.1);
  box-shadow: 0 2px 8px rgba(10, 26, 47, 0.08);
}

.ease-survey-radio {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.ease-survey-num {
  font-size: 1.1rem;
  font-weight: var(--font-weight-bold);
  color: rgba(10, 26, 47, 0.88);
}

.ease-survey-label {
  font-size: 0.62rem;
  text-align: center;
  line-height: 1.2;
  color: var(--bank-gray-600);
  display: none;
}

@media (min-width: 420px) {
  .ease-survey-label {
    display: block;
  }
}

.ease-survey-scale-hint {
  display: flex;
  justify-content: space-between;
  margin-top: 0.35rem;
  font-size: 0.7rem;
  color: var(--bank-gray-500);
}

.ease-survey-comment-label {
  display: block;
  font-size: 0.8rem;
  font-weight: var(--font-weight-medium);
  margin-bottom: 0.25rem;
  color: rgba(10, 26, 47, 0.85);
}

.ease-survey-comment {
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 1rem;
  padding: 0.5rem 0.65rem;
  border-radius: 10px;
  border: 1px solid rgba(10, 26, 47, 0.14);
  font-family: inherit;
  font-size: 0.875rem;
  resize: vertical;
  min-height: 2.75rem;
}

.ease-survey-comment:focus {
  outline: none;
  border-color: rgba(212, 175, 55, 0.65);
  box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.15);
}

.ease-survey-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
}

.ease-survey-btn {
  padding: 0.45rem 0.85rem;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  border: 1px solid transparent;
  font-family: inherit;
}

.ease-survey-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.ease-survey-btn--ghost {
  background: rgba(10, 26, 47, 0.04);
  border-color: rgba(10, 26, 47, 0.12);
  color: rgba(10, 26, 47, 0.85);
}

.ease-survey-btn--ghost:hover:not(:disabled) {
  background: rgba(212, 175, 55, 0.1);
  border-color: rgba(212, 175, 55, 0.35);
}

.ease-survey-btn--primary {
  background: linear-gradient(135deg, rgba(10, 26, 47, 0.96), rgba(26, 34, 56, 0.96));
  border-color: rgba(212, 175, 55, 0.35);
  color: #fff;
}

.ease-survey-btn--primary:hover:not(:disabled) {
  border-color: rgba(212, 175, 55, 0.55);
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.ease-survey-fade-enter-active,
.ease-survey-fade-leave-active {
  transition: opacity 0.22s ease;
}

.ease-survey-fade-enter-from,
.ease-survey-fade-leave-to {
  opacity: 0;
}
</style>
